import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatINR, track } from "../lib/api";
import { useStore } from "../lib/store";
import { useSEO } from "../lib/seo";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function CartPage() {
  const { cart, updateQty, removeFromCart, subtotal, coupon, setCoupon, settings, restoreCart } = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [couponMsg, setCouponMsg] = useState("");
  useSEO({ title: "Your bag — Haven Hives", description: "Review your Haven Hives bag before checkout." });

  // Abandoned-cart reminder link: /cart?recover=<token> restores the saved bag.
  const recover = params.get("recover");
  useEffect(() => {
    if (!recover) return;
    api.get(`/razorpay/magic/recover/${recover}`).then((r) => {
      if (r.data.lines?.length) { restoreCart(r.data.lines); toast.success("Welcome back! We've restored your bag."); }
    }).catch(() => toast.error("That recovery link has expired — your bag below is the latest one."))
      .finally(() => setParams({}, { replace: true }));
  }, [recover]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoupon = async () => {
    setCouponMsg("");
    try {
      const r = await api.post("/coupons/validate", { code: coupon?.code || "" });
      setCouponMsg(`${r.data.code} will be applied at checkout.`);
    } catch (e) { setCouponMsg(e.response?.data?.detail || "That code isn't valid."); }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-xl mx-auto text-center py-24 px-5" data-testid="cart-empty">
        <div className="w-20 h-20 rounded-full bg-surface flex items-center justify-center mx-auto"><ShoppingBag className="w-8 h-8" /></div>
        <h1 className="font-display text-3xl font-semibold mt-6">Your bag is empty</h1>
        <p className="text-charcoal/70 mt-2">Little things are waiting — bottles, bento boxes and bursts of stationery joy.</p>
        <Link to="/collections/shop-all" className="inline-block mt-6 bg-forest text-white hover:bg-forest-dark px-8 py-3.5 rounded-full" data-testid="cart-empty-cta">Start shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="cart-page">
      <h1 className="font-display font-semibold text-3xl sm:text-4xl">Your bag</h1>
      <div className="grid lg:grid-cols-3 gap-10 mt-8">
        <ul className="lg:col-span-2 space-y-4" data-testid="cart-page-items">
          {cart.map((l) => (
            <li key={l.variant_id} className="flex gap-4 bg-white rounded-3xl card-outline p-4">
              <Link to={`/products/${l.slug}`}><img src={l.image} alt={l.image_alt} width="96" height="96" className="w-24 h-24 rounded-2xl object-cover" loading="lazy" /></Link>
              <div className="flex-1 min-w-0">
                <Link to={`/products/${l.slug}`} className="font-semibold leading-snug hover:text-forest">{l.name}</Link>
                <p className="text-sm text-charcoal/60 mt-0.5">{l.variant_name}</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center gap-1 border border-charcoal/15 rounded-full px-1.5">
                    <button data-testid={`page-qty-minus-${l.variant_id}`} onClick={() => updateQty(l.variant_id, l.qty - 1)} aria-label={`Decrease quantity of ${l.name}`} className="p-1.5"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="text-sm font-semibold w-5 text-center">{l.qty}</span>
                    <button data-testid={`page-qty-plus-${l.variant_id}`} onClick={() => updateQty(l.variant_id, l.qty + 1)} aria-label={`Increase quantity of ${l.name}`} className="p-1.5"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button data-testid={`page-remove-${l.variant_id}`} onClick={() => removeFromCart(l.variant_id)} className="flex items-center gap-1 text-xs font-semibold text-charcoal/50 hover:text-coral" aria-label={`Remove ${l.name}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold">{formatINR(l.price * l.qty)}</p>
                {l.qty > 1 && <p className="text-xs text-charcoal/50 mt-1">{formatINR(l.price)} each</p>}
              </div>
            </li>
          ))}
        </ul>

        <aside className="bg-white rounded-3xl card-outline p-5 h-fit sticky top-24" aria-label="Order summary">
          <h2 className="font-display font-semibold text-xl">Summary</h2>
          <div className="mt-4 flex gap-2">
            <input value={coupon?.code || ""} onChange={(e) => setCoupon({ code: e.target.value.toUpperCase() })}
              placeholder="Coupon code" aria-label="Coupon code" data-testid="cart-page-coupon-input"
              className="flex-1 min-w-0 border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" />
            <button onClick={applyCoupon} data-testid="cart-page-coupon-apply" className="bg-forest text-white text-sm font-semibold px-4 py-2.5 rounded-full">Apply</button>
          </div>
          {couponMsg && <p role="status" className="text-xs mt-2 text-charcoal/70" data-testid="coupon-message">{couponMsg}</p>}
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-charcoal/70">Subtotal</dt><dd className="font-semibold" data-testid="cart-subtotal">{formatINR(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-charcoal/70">Shipping</dt><dd>Calculated at checkout</dd></div>
          </dl>
          <p className="text-xs text-charcoal/50 mt-2">{settings?.tax_wording || "Prices include GST."}</p>
          <button onClick={() => { track("begin_checkout", { value: subtotal, currency: "INR" }); navigate("/checkout"); }}
            data-testid="cart-page-checkout-btn"
            className="mt-5 w-full bg-forest text-white font-display text-base hover:bg-forest-dark py-3.5 rounded-full transition-colors">
            Checkout
          </button>
        </aside>
      </div>
    </div>
  );
}
